import { supabase } from './supabase'

/**
 * Tournament activity feed: a thin layer over the group_chats / group_chat_messages
 * tables, scoped to a tournament. Viewing is public; posting/reacting is restricted
 * to joined members by RLS. System "join" entries are message_type='join'.
 */

export interface ActivityUser {
  id: string
  gamertag: string
  profile_image_url: string | null
}

export interface ActivityReaction {
  emoji: string
  count: number
  reactedByMe: boolean
}

export interface ActivityItem {
  id: string
  type: 'user' | 'join'
  content: string
  createdAt: string
  sender: ActivityUser | null
  reactions: ActivityReaction[]
}

/** Resolve the group chat id for a tournament (null if no one has joined yet). */
export async function getTournamentChatId(tournamentId: string): Promise<string | null> {
  const { data } = await supabase
    .from('group_chats')
    .select('id')
    .eq('tournament_id', tournamentId)
    .maybeSingle()
  return data?.id ?? null
}

/** Is the current user a member of this chat? (RLS only returns own membership.) */
export async function isChatMember(chatId: string, userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('group_chat_members')
    .select('user_id')
    .eq('group_chat_id', chatId)
    .eq('user_id', userId)
    .maybeSingle()
  return !!data
}

/** The lobby code — RLS returns a row only for joined members (or organizers). */
export async function getLobbyCode(tournamentId: string): Promise<string | null> {
  const { data } = await supabase
    .from('tournament_lobby_codes')
    .select('lobby_code')
    .eq('tournament_id', tournamentId)
    .maybeSingle()
  return data?.lobby_code ?? null
}

/**
 * Set (or update) a tournament's lobby code. Organizer-only via RLS. Pass an
 * empty string to clear it. One row per tournament, keyed on tournament_id.
 */
export async function setLobbyCode(
  tournamentId: string,
  code: string
): Promise<{ ok: true } | { error: string }> {
  const trimmed = code.trim()
  if (!trimmed) {
    const { error } = await supabase
      .from('tournament_lobby_codes')
      .delete()
      .eq('tournament_id', tournamentId)
    if (error) return { error: error.message }
    return { ok: true }
  }
  const { error } = await supabase
    .from('tournament_lobby_codes')
    .upsert(
      { tournament_id: tournamentId, lobby_code: trimmed },
      { onConflict: 'tournament_id' }
    )
  if (error) {
    console.error('setLobbyCode error:', error.message)
    return { error: error.message }
  }
  return { ok: true }
}

/** Full activity feed for a chat: messages + senders + aggregated reactions. */
export async function getActivity(
  chatId: string,
  currentUserId: string | null
): Promise<ActivityItem[]> {
  const { data: messages } = await supabase
    .from('group_chat_messages')
    .select('id, sender_id, content, created_at, message_type')
    .eq('group_chat_id', chatId)
    .order('created_at', { ascending: true })

  if (!messages || messages.length === 0) return []

  const senderIds = Array.from(new Set(messages.map((m) => m.sender_id)))
  const messageIds = messages.map((m) => m.id)

  const [{ data: users }, { data: reactions }] = await Promise.all([
    supabase.from('users').select('id, gamertag, profile_image_url').in('id', senderIds),
    supabase
      .from('group_chat_message_reactions')
      .select('message_id, emoji, user_id')
      .in('message_id', messageIds),
  ])

  const userMap = new Map<string, ActivityUser>()
  for (const u of users ?? []) userMap.set(u.id, u as ActivityUser)

  // message_id -> emoji -> { count, reactedByMe }
  const reactionMap = new Map<string, Map<string, { count: number; reactedByMe: boolean }>>()
  for (const r of reactions ?? []) {
    if (!reactionMap.has(r.message_id)) reactionMap.set(r.message_id, new Map())
    const byEmoji = reactionMap.get(r.message_id)!
    const cur = byEmoji.get(r.emoji) ?? { count: 0, reactedByMe: false }
    cur.count += 1
    if (currentUserId && r.user_id === currentUserId) cur.reactedByMe = true
    byEmoji.set(r.emoji, cur)
  }

  return messages.map((m) => ({
    id: m.id,
    type: (m.message_type as 'user' | 'join') ?? 'user',
    content: m.content,
    createdAt: m.created_at,
    sender: userMap.get(m.sender_id) ?? null,
    reactions: Array.from(reactionMap.get(m.id)?.entries() ?? []).map(([emoji, v]) => ({
      emoji,
      count: v.count,
      reactedByMe: v.reactedByMe,
    })),
  }))
}

/** Post a comment. RLS enforces membership + sender_id = auth.uid(). */
export async function postComment(chatId: string, userId: string, content: string) {
  const { error } = await supabase.from('group_chat_messages').insert({
    group_chat_id: chatId,
    sender_id: userId,
    content,
    message_type: 'user',
  })
  if (error) throw new Error(error.message)
  await supabase
    .from('group_chats')
    .update({ last_message_at: new Date().toISOString() })
    .eq('id', chatId)
}

/** Toggle a single emoji reaction for the current user on a message. */
export async function toggleReaction(
  messageId: string,
  userId: string,
  emoji: string,
  currentlyReacted: boolean
) {
  if (currentlyReacted) {
    await supabase
      .from('group_chat_message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
  } else {
    await supabase
      .from('group_chat_message_reactions')
      .insert({ message_id: messageId, user_id: userId, emoji })
  }
}
